// src/context/GoogleAuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';

const GoogleAuthContext = createContext();

export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);
  if (!context) {
    throw new Error('useGoogleAuth must be used within GoogleAuthProvider');
  }
  return context;
};

export const GoogleAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        console.log('✅ Existing session found');
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // Google Login - Popup flow with POST to backend
  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        console.log('🔐 Google login successful, sending token to backend...');
        
        const backendUrl = process.env.REACT_APP_API_URL || 'https://krishidnya-backend.onrender.com/api';
        
        // POST to your backend's google endpoint
        const response = await fetch(`${backendUrl}/auth/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessToken: tokenResponse.access_token
          }),
        });

        const data = await response.json();
        
        if (data.success) {
          console.log('✅ Backend authentication successful');
          
          // Store the backend JWT token and user data
          localStorage.setItem('token', data.data.token);
          localStorage.setItem('user', JSON.stringify(data.data.user));
          setUser(data.data.user);
          
          // Redirect to dashboard
          window.location.href = '/dashboard';
        } else {
          console.error('❌ Backend auth failed:', data.message);
          throw new Error(data.message || 'Authentication failed');
        }
      } catch (error) {
        console.error('❌ Google login error:', error);
        window.location.href = '/login?error=google_auth_failed';
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      console.error('❌ Google Login Failed:', error);
      setLoading(false);
      window.location.href = '/login?error=google_auth_failed';
    },
    flow: 'implicit',
  });

  // Logout user
  const logout = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        const backendUrl = process.env.REACT_APP_API_URL || 'https://krishidnya-backend.onrender.com/api';
        try {
          await fetch(`${backendUrl}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        } catch (error) {
          console.warn('Backend logout failed:', error);
        }
      }
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      
      window.location.href = '/';
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user
  };

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
    </GoogleAuthContext.Provider>
  );
};

export default GoogleAuthContext;
